import type { ReactNode } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/AuthContext";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/Login";
import { SignupPage } from "./pages/Signup";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { PlannerPage } from "./pages/Planner";
import { ChannelsPage } from "./pages/Channels";
import { ReportsPage } from "./pages/Reports";
import { StudyPage } from "./pages/Study";
import { SettingsPage } from "./pages/Settings";

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageBlank />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageBlank />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function FullPageBlank() {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
      Loading…
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthed>
            <SignupPage />
          </RedirectIfAuthed>
        }
      />
      {/* Not RedirectIfAuthed: a logged-in user (active session) must still be able
          to request/complete a password reset. The recovery email links here with a
          ?token=, and reset signs out existing sessions on success. Wrapping these in
          RedirectIfAuthed bounced authed users to "/" — so the reset form never showed. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<PlannerPage />} />
        <Route path="channels" element={<ChannelsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="study" element={<StudyPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
